import { useState } from "react";
import { ChevronDown, ChevronUp, HelpCircle } from "lucide-react";

const faqs = [
  {
    question: "How does Metefy's QR technology work?",
    answer: "Metefy uses advanced QR codes that connect finders directly to item owners. Simply scan the code with any smartphone camera, and you'll be instantly connected through our secure platform."
  },
  {
    question: "Is my personal information secure?",
    answer: "Absolutely. Metefy prioritizes your privacy and security. You control what information is shared, and all communications are encrypted. Your personal details are never publicly displayed."
  },
  {
    question: "Do I need to download an app?",
    answer: "No app required! Metefy works with any smartphone's built-in camera. Finders simply scan the QR code, and the connection happens instantly through their web browser."
  },

  {
    question: "Can I use it on any type of item?",
    answer: "Yes! Metefy works on metro cards, laptops, bags, keys, electronics, and virtually any item you want to protect. Our QR stickers are durable and weather-resistant."
  },
  {
    question: "What happens if someone finds my item?",
    answer: "When someone scans your QR code, they're taken to a secure page where they can contact you directly through our platform or social media, ensuring a quick and safe reunion."
  },
  {
    question: "How long do the QR stickers last?",
    answer: "Our QR stickers are made with high-quality, weather-resistant materials designed to last for years. They're waterproof and can withstand daily wear and tear."
  },
  {
    question: "Can I update my contact information later?",
    answer: "Yes, absolutely! You can update your contact information, social media links, and preferences anytime through your Metefy profile. Changes are reflected immediately."
  },
  {
    question: "What if I don't have a metro card?",
    answer: "No problem! You can purchase Metefy tokens separately that work exactly like metro card numbers. These tokens can be attached to any item you want to protect."
  }
];

export function AboutFaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Generate FAQ Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <section id="faq" className="pt-24 pb-0 bg-card">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-lg text-foreground font-semibold text-sm mb-6">
            <HelpCircle className="w-4 h-4 text-foreground" />
            <span className="text-foreground">FAQ</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            Frequently Asked <span className="text-primary">Questions</span>
          </h2>
          <p className="text-xl text-foreground/80 max-w-3xl mx-auto">
            Find answers to common questions about Metefy's digital identity platform
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          {faqs.map((faq, index) => (
            <div key={index} className="mb-4">
              <button
                onClick={() => toggleFaq(index)}
                className="w-full bg-card border-2 border-border hover:border-primary rounded-xl p-6 text-left transition-all duration-300 group"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors duration-300">
                    {faq.question}
                  </h3>
                  {openIndex === index ? (
                    <ChevronUp className="w-5 h-5 text-primary flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-primary group-hover:scale-110 transition-all duration-300 flex-shrink-0" />
                  )}
                </div>
                {openIndex === index && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-foreground/80 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </button>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-foreground/80 mb-4">
            Still have questions? We're here to help.
          </p>
          <a
            href="https://wa.me/918307562996?text=Hello!%20I%20have%20a%20question%20or%20doubt%20about%20Metefy.%20Can%20you%20please%20help%20me?"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-primary hover:opacity-80 font-semibold transition-opacity duration-300"
          >
            Contact Us
            <HelpCircle className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Inject FAQ Schema for SEO - hidden from users */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        aria-hidden="true"
      ></script>
    </section>
  );
} 